import {
    ColumnDefResolved,
    SortingState,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import { useEffect, useState } from "react"

import getObjectCardsViewConfigStorageKey from "@utils/getObjectCardsViewConfigStorageKey"
import getObjectTableViewConfigStorageKey from "@utils/getObjectTableViewConfigStorageKey"

import useCardsViewConfig from "@organisms/ObjectsView/CardsView/hooks/useCardsViewConfig"
import { ObjectsData } from "@organisms/ObjectsView/ObjectsView.types"
import useViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider.context"

import { ScheduleHorizon } from "@constants/scheduleHorizon"

import { TableContext } from "./Table.context"
import { TableContextValues, TableProviderProps, TableViewConfig } from "./Table.types"
import useScheduleHorizon from "./hooks/useScheduleHorizon"
import useTableColumns from "./hooks/useTableColumns"
import useTableData from "./hooks/useTableData"
import useTableSortingState from "./hooks/useTableSortingState"
import useTableState from "./hooks/useTableState"
import useTableViewConfig from "./hooks/useTableViewConfig"
import useUnfilteredTabCount from "./hooks/useUnfilteredTabCount"

export default function TableProvider<T = ObjectsData>(props: TableProviderProps) {
    const {
        children,
        activePhaseStorageKey,
        activeTabStorageKey,
        searchKeywordsStorageKey,
        listTitle,
        listDataFilters,
        objectType,
        objectTypeVariation,
        objectName,
        objectCreateRoute,
        objectIndexName,
        defaultActivePhase,
        defaultTableConfigOverride,
        isRowClickable,
        isEmptyStateCreateButtonVisible,
        onRowHoverChange,
    } = props

    const {
        activePhase,
        activeTab,
        searchKeywords,
        filters,
        setActivePhase,
        setActiveTab,
        setSearchKeywords,
        setFilters,
    } = useTableState({
        activePhaseStorageKey,
        activeTabStorageKey,
        searchKeywordsStorageKey,
        defaultActivePhase,
        defaultFilters: listDataFilters,
    })

    // The view menu's open state lives here so the list edge states can open it too.
    const [isViewMenuOpen, setIsViewMenuOpen] = useState<boolean>(false)

    const tabContentName = `${activeTab?.title} ${listTitle}`

    const viewConfigStorageKeyArgs = {
        objectType: objectType,
        objectTypeVariation: objectTypeVariation,
        phase: activePhase,
        tab: activeTab?.title ?? "",
    }

    const { currentTableViewConfig, updateCurrentTableViewConfig, getTableViewConfig } = useTableViewConfig(
        getObjectTableViewConfigStorageKey(viewConfigStorageKeyArgs),
        defaultTableConfigOverride,
    )
    const { currentCardsViewConfig, updateCardsViewConfig, getCardsViewConfig } = useCardsViewConfig(
        getObjectCardsViewConfigStorageKey(viewConfigStorageKeyArgs),
    )

    // Each view type remembers its sort in its own config, so the same tab can be ordered one way as a
    // table and another way as cards. A view with no config of its own runs on the tab's default
    // ordering and has nowhere to keep a change.
    const { activeViewType } = useViewTypesProvider()
    let savedSorting: SortingState | undefined
    let saveSorting: ((sorting: SortingState) => void) | undefined
    if (activeViewType === "table") {
        savedSorting = currentTableViewConfig?.sorting
        saveSorting = (sorting) => updateCurrentTableViewConfig("sorting", sorting)
    } else if (activeViewType === "cards") {
        savedSorting = currentCardsViewConfig?.sorting
        saveSorting = (sorting) => updateCardsViewConfig("sorting", sorting)
    }

    const {
        sorting,
        onSortingChange,
        setSortingChanged,
        sortingChanged,
        setCurrentSortToAscending,
        setCurrentSortToDescending,
        onSortingItemSelection,
    } = useTableSortingState<T>({
        activeTab,
        savedSorting,
        saveSorting,
    })

    // The schedule horizon is a view setting like the sort, so each view type keeps its own per tab.
    let savedScheduleHorizon: ScheduleHorizon | undefined
    let saveScheduleHorizon: ((horizon: ScheduleHorizon) => void) | undefined
    let getSavedScheduleHorizonForTab: ((tab: string) => ScheduleHorizon | undefined) | undefined
    if (activeViewType === "table") {
        savedScheduleHorizon = currentTableViewConfig?.scheduleHorizon
        saveScheduleHorizon = (horizon) => updateCurrentTableViewConfig("scheduleHorizon", horizon)
        getSavedScheduleHorizonForTab = (tab) =>
            getTableViewConfig(getObjectTableViewConfigStorageKey({ ...viewConfigStorageKeyArgs, tab }))
                ?.scheduleHorizon
    } else if (activeViewType === "cards") {
        savedScheduleHorizon = currentCardsViewConfig?.scheduleHorizon
        saveScheduleHorizon = (horizon) => updateCardsViewConfig("scheduleHorizon", horizon)
        getSavedScheduleHorizonForTab = (tab) =>
            getCardsViewConfig(getObjectCardsViewConfigStorageKey({ ...viewConfigStorageKeyArgs, tab }))
                ?.scheduleHorizon
    }

    const {
        scheduleHorizon,
        setScheduleHorizon,
        isScheduleHorizonAvailable,
        scheduleHorizonUpperBound,
        listFilters,
        getFiltersForTab,
    } = useScheduleHorizon({
        objectType,
        activePhase,
        savedScheduleHorizon,
        saveScheduleHorizon,
        getSavedScheduleHorizonForTab,
        filters,
    })

    const {
        currentData,
        error,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
        isEmpty,
    } = useTableData<T>({
        activeTab,
        tabContentName,
        searchKeywords,
        filters: listFilters,
        sorting,
    })

    // An empty list with a horizon set and rows behind it is the horizon's doing, not an empty tab.
    const { unfilteredTabCount, isUnfilteredTabCountLoading } = useUnfilteredTabCount({
        activeTab,
        tabContentName,
        searchKeywords,
        filters,
        isEnabled: isEmpty && !!scheduleHorizonUpperBound,
    })
    const hiddenByScheduleHorizonCount = unfilteredTabCount ?? 0
    const isHiddenByScheduleHorizon = isEmpty && !!scheduleHorizonUpperBound && hiddenByScheduleHorizonCount > 0

    // The list stays loading until that count settles, so the plain empty state never shows first.
    const isListLoading = isLoading || isUnfilteredTabCountLoading
    const isListEmpty = isEmpty && !isUnfilteredTabCountLoading

    const {
        setColumns,
        tableColumns,
        reachedPinLimit,
        updateColumnVisibility,
        unpinColumn,
        pinColumn,
        updateColumnPinningOrder,
        updateColumnOrder,
        invisibleColumns,
        reachedMinimumOfColumnsVisible,
        pinnedColumns,
        unpinnedColumns,
        columnsNamesList,
        visibleColumns,
        activeSortingColumn,
        activeSorting,
        sortingEnabledColumns,
        activeSortingIcons,
        activeSortingLabels,
    } = useTableColumns<T>({
        currentTableViewConfig: currentTableViewConfig as TableViewConfig,
        updateCurrentTableViewConfig,
    })

    const table = useReactTable({
        columns: tableColumns,
        data: currentData,
        getCoreRowModel: getCoreRowModel(),
        initialState: !currentTableViewConfig
            ? undefined
            : {
                  sorting: currentTableViewConfig?.sorting,
                  columnVisibility: currentTableViewConfig?.columnVisibility,
                  columnPinning: currentTableViewConfig?.columnPinning,
                  columnOrder: currentTableViewConfig?.columnOrder,
              },
        onSortingChange,
        getSortedRowModel: getSortedRowModel(),
        manualSorting: true,
        debugHeaders: true,
        debugColumns: true,
    })

    useEffect(() => {
        if (currentTableViewConfig) {
            const newColumnPinning = [...(currentTableViewConfig.columnPinning?.left ?? [])]

            table.setState((state) => {
                return {
                    ...state,
                    ...currentTableViewConfig,
                    columnPinning: {
                        left: newColumnPinning,
                    },
                }
            })
        }
    }, [currentTableViewConfig])

    const contextValue: TableContextValues<T> = {
        data: currentData,
        columns: tableColumns as ColumnDefResolved<T>[],
        setColumns,
        table,
        hasNextPage,
        activePhase,
        setActivePhase,
        activeTab,
        setActiveTab,
        searchKeywords,
        setSearchKeywords,
        filters: listFilters,
        setFilters,
        getFiltersForTab,
        tabContentName,
        error,
        isLoading: isListLoading,
        isError,
        fetchNextPage,
        isFetchingNextPage,
        refetch,
        objectName,
        objectCreateRoute,
        objectTypeVariation,
        isSearching: !!searchKeywords,
        isEmpty: isListEmpty,
        reachedPinLimit,
        updateColumnVisibility,
        unpinColumn,
        pinColumn,
        updateColumnPinningOrder,
        updateColumnOrder,
        setSortingChanged,
        sortingChanged,
        invisibleColumns,
        reachedMinimumOfColumnsVisible,
        pinnedColumns,
        unpinnedColumns,
        columnsNamesList,
        columnsOrder: currentTableViewConfig?.columnOrder ?? [],
        objectType,
        currentTableViewConfig,
        objectIndexName,
        visibleColumns: visibleColumns as ColumnDefResolved<T>[],
        sorting,
        onSortingChange,
        activeSorting,
        setCurrentSortToAscending,
        setCurrentSortToDescending,
        onSortingItemSelection,
        sortingEnabledColumns,
        activeSortingIcons,
        activeSortingLabels,
        activeSortingColumn,
        defaultTableConfigOverride,
        isRowClickable,
        isEmptyStateCreateButtonVisible,
        onRowHoverChange,
        scheduleHorizon,
        setScheduleHorizon,
        isScheduleHorizonAvailable,
        isHiddenByScheduleHorizon,
        hiddenByScheduleHorizonCount,
        isViewMenuOpen,
        setIsViewMenuOpen,
    }

    return <TableContext.Provider value={contextValue}>{children}</TableContext.Provider>
}
